import { useParams } from "@tanstack/react-router";
import { ThreeDots } from "react-loader-spinner";

import { FeatureAccessNotice } from "@components/FeatureAccessNotice";

import { CAPABILITIES } from "@constants/capabilities";

import { useCapabilities } from "@hooks/queries/use-me";

import AudioTranscriberPage from "./AudioTranscriberPage";

/**
 * Route entry for the Assessment Scribe.
 *
 * Remounts the whole page per case via `key={caseNumber}` (MDRS-155): switching
 * cases is a route-param change on the same `/report-studio/$id/assessment-scribe`
 * route, so TanStack reuses the page instance. Without the key, the page's
 * per-case local state (edits, summaries, live segments, session notes, the
 * autosave interval in useAutoExport) survives the switch — showing the previous
 * case's content and, worse, letting an autosave persist it onto the newly
 * selected case. Keying by caseNumber forces a fresh mount so all that state and
 * its timers reset cleanly.
 *
 * MDRS-443: the case-detail tab bar only hides this tab's *trigger* when the
 * capability is off — it never blocks the routed content itself, so a tab
 * already open when the capability is revoked, or a direct URL nav straight
 * to this route, both reached the page regardless. Re-checked here, at the
 * route entry, rather than deeper in the page.
 *
 * `/users/me` starts with an empty capability set while it's still loading,
 * so `can()` would read false transiently — checking `isLoading` first (the
 * same pattern as `AdminRoute`) avoids flashing the access notice at a
 * fully-authorized user on every cold load.
 */
const AudioTranscriberRoute = () => {
  const { id: caseNumber } = useParams({ strict: false }) as { id: string };
  const { can, isLoading } = useCapabilities();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <ThreeDots />
      </div>
    );
  }

  if (!can(CAPABILITIES.ASSESSMENT_SCRIBE)) {
    return (
      <FeatureAccessNotice
        title="You don't have access to Assessment Scribe."
        body="Ask an admin to enable this feature for your account."
      />
    );
  }

  return <AudioTranscriberPage key={caseNumber} />;
};

export default AudioTranscriberRoute;
